import * as XLSX from "xlsx";

function slugify(name) {
  return String(name || "report")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 80);
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function flattenSnapshot(snapshot) {
  const sheets = {};
  const overview = snapshot?.overview || {};
  sheets.Overview = [
    ["Field", "Value"],
    ...Object.entries(overview).map(([key, value]) => [
      key,
      typeof value === "object" ? JSON.stringify(value) : value,
    ]),
  ];

  const report = snapshot?.report || {};
  Object.entries(report).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      if (value.length === 0) {
        sheets[key] = [["(empty)"]];
        return;
      }
      if (typeof value[0] === "object" && value[0] !== null) {
        const headers = Object.keys(value[0]);
        sheets[key] = [headers, ...value.map((row) => headers.map((h) => row[h] ?? ""))];
      } else {
        sheets[key] = [["value"], ...value.map((v) => [v])];
      }
      return;
    }
    if (value && typeof value === "object") {
      sheets[key] = [
        ["label", "total"],
        ...Object.entries(value).map(([label, total]) => [label, total]),
      ];
      return;
    }
    sheets[key] = [
      ["Field", "Value"],
      [key, value],
    ];
  });

  if (Array.isArray(snapshot?.entries) && snapshot.entries.length) {
    const rows = snapshot.entries;
    const headers = Object.keys(rows[0]);
    sheets.AuditTrail = [headers, ...rows.map((row) => headers.map((h) => row[h] ?? ""))];
  }

  if (Array.isArray(snapshot?.notifications) && snapshot.notifications.length) {
    const rows = snapshot.notifications;
    const headers = ["title", "message", "actor_name", "event_type", "created_at"];
    sheets.Activity = [headers, ...rows.map((row) => headers.map((h) => row[h] ?? ""))];
  }

  if (snapshot?.summary) {
    const byDay = snapshot.summary.by_day || [];
    sheets.ActivityByDay = [["day", "count"], ...byDay.map((r) => [r.day, r.count])];
    const byType = snapshot.summary.by_event_type || [];
    sheets.ActivityByType = [
      ["event_type", "title", "count"],
      ...byType.map((r) => [r.event_type, r.title, r.count]),
    ];
    const actors = snapshot.summary.top_actors || [];
    sheets.TopActors = [["actor_name", "count"], ...actors.map((r) => [r.actor_name, r.count])];
  }

  return sheets;
}

function buildWorkbook(snapshot) {
  const wb = XLSX.utils.book_new();
  const sheets = flattenSnapshot(snapshot);
  Object.entries(sheets).forEach(([name, rows]) => {
    const safeName = name.slice(0, 31);
    const ws = XLSX.utils.aoa_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, safeName);
  });
  return wb;
}

function snapshotToText(snapshot) {
  const lines = [
    `Report: ${snapshot?.title || "Dashboard export"}`,
    `Generated: ${snapshot?.generated_at || new Date().toISOString()}`,
    `Type: ${snapshot?.type || "Dashboard"}`,
    `Period: ${snapshot?.period || ""}`,
    "",
  ];
  const sheets = flattenSnapshot(snapshot);
  Object.entries(sheets).forEach(([name, rows]) => {
    lines.push(`## ${name}`);
    rows.forEach((row) => lines.push(row.map((c) => String(c ?? "")).join("\t")));
    lines.push("");
  });
  return lines.join("\n");
}

function snapshotToHtml(snapshot) {
  const sheets = flattenSnapshot(snapshot);
  const sections = Object.entries(sheets)
    .map(([name, rows]) => {
      const body = rows
        .map(
          (row, i) =>
            `<tr>${row
              .map(
                (cell) =>
                  `<t${i === 0 ? "h" : "d"} style="border:1px solid #ddd;padding:6px;text-align:left">${String(
                    cell ?? ""
                  )}</t${i === 0 ? "h" : "d"}>`
              )
              .join("")}</tr>`
        )
        .join("");
      return `<h2>${name}</h2><table style="border-collapse:collapse;width:100%;margin-bottom:24px">${body}</table>`;
    })
    .join("");

  return `<!DOCTYPE html><html><head><title>${snapshot?.title || "Report"}</title>
    <style>body{font-family:system-ui,sans-serif;padding:24px;color:#1f2937}h1{font-size:20px}h2{font-size:14px;margin-top:20px}</style>
    </head><body>
    <h1>${snapshot?.title || "Report"}</h1>
    <p>${snapshot?.type || ""} · ${snapshot?.period || ""} · ${snapshot?.generated_at || ""}</p>
    ${sections}
    <script>window.onload=function(){window.print()}</script>
    </body></html>`;
}

/**
 * Export a report snapshot as Excel, CSV, Text, or PDF (print dialog).
 * @param {"Excel"|"CSV"|"Text"|"TXT"|"PDF"|string} format
 * @param {object} snapshot
 * @param {string} [filenameBase]
 */
export function exportReport(format, snapshot, filenameBase = "report") {
  const normalized = String(format || "Excel").toUpperCase();
  const base = slugify(filenameBase);
  const payload = {
    ...snapshot,
    generated_at: snapshot?.generated_at || new Date().toISOString(),
    title: snapshot?.title || filenameBase,
  };

  if (normalized === "EXCEL" || normalized === "XLSX") {
    const wb = buildWorkbook(payload);
    const array = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([array], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    triggerDownload(blob, `${base}.xlsx`);
    return { format: "Excel", filename: `${base}.xlsx`, size: blob.size };
  }

  if (normalized === "CSV") {
    const wb = buildWorkbook(payload);
    const firstSheet = wb.SheetNames[0];
    const csv = XLSX.utils.sheet_to_csv(wb.Sheets[firstSheet]);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    triggerDownload(blob, `${base}.csv`);
    return { format: "CSV", filename: `${base}.csv`, size: blob.size };
  }

  if (normalized === "TEXT" || normalized === "TXT") {
    const text = snapshotToText(payload);
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    triggerDownload(blob, `${base}.txt`);
    return { format: "Text", filename: `${base}.txt`, size: blob.size };
  }

  const html = snapshotToHtml(payload);
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const win = window.open(url, "_blank", "noopener,noreferrer");
  if (!win) {
    triggerDownload(blob, `${base}.html`);
  }
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
  return { format: "PDF", filename: `${base}.pdf`, size: blob.size };
}
